import React, { useState } from "react";
import { ComposableMap, Geographies, Geography, Marker } from "react-simple-maps";
import { scaleQuantize } from "d3-scale";

// URL estável e pública do GeoJSON com os estados do Brasil
const geoUrl = "https://raw.githubusercontent.com/codeforamerica/click_that_hood/master/public/data/brazil-states.geojson";

// Dados de preenchimento dos estados (Tons de fundo)
const dadosEstados = {
  SP: 150, RJ: 120, MG: 95, BA: 80, RS: 75,
  PR: 70, PE: 65, CE: 60, AM: 40, DF: 110,
  SC: 90, GO: 85, ES: 50, PA: 45, MA: 30,
  MT: 55, MS: 62, RN: 48, PB: 42, AL: 38,
  SE: 35, PI: 28, TO: 25, RO: 20, AC: 15,
  AP: 12, RR: 10
};

// Escala de cores para o fundo dos estados
const colorScale = scaleQuantize()
  .domain([0, 150])
  .range([
    "#1c222a", // Cinza bem escuro para destacar os pins
    "#212830",
    "#262e38",
    "#2b3440"
  ]);

// 1. Dados dos Vendedores e suas localizações (Latitude, Longitude)
// Use as coordenadas reais ou aproximadas das cidades/vendas
const vendasVendedores = [
  { nome: "Carlos", cidade: "São Paulo - SP", coordenadas: [-46.6333, -23.5505], cor: "#00E5FF", valor: "R$ 15.000,00" },
  { nome: "Ana", cidade: "Rio de Janeiro - RJ", coordenadas: [-43.1729, -22.9068], cor: "#FF007F", valor: "R$ 12.500,00" },
  { nome: "Bruno", cidade: "Belo Horizonte - MG", coordenadas: [-43.9352, -19.9208], cor: "#FFD700", valor: "R$ 8.900,00" },
  { nome: "Carlos", cidade: "Belo Horizonte - MG", coordenadas: [-42.9352, -16.9208], cor: "#FFD700", valor: "R$ 8.900,00" },
  { nome: "Carlos", cidade: "Salvador - BA", coordenadas: [-38.5016, -12.9714], cor: "#00E5FF", valor: "R$ 7.200,00" },
  { nome: "Ana", cidade: "Porto Alegre - RS", coordenadas: [-51.2287, -30.0346], cor: "#FF007F", valor: "R$ 9.100,00" },
  { nome: "Mariana", cidade: "Recife - PE", coordenadas: [-34.8770, -8.0542], cor: "#7000FF", valor: "R$ 11.400,00" },
  { nome: "Bruno", cidade: "Goiânia - GO", coordenadas: [-49.2550, -16.6869], cor: "#FFD700", valor: "R$ 5.400,00" },
];

export default function MapaBrasil() {
  const [estadoSelecionado, setEstadoSelecionado] = useState(null);
  const [alfineteSelecionado, setAlfineteSelecionado] = useState(null);

  return (
    <div className="w-full max-w-[550px] mx-auto text-center p-4">
      <h2 className="text-xl font-bold mb-2 text-white">Vendas por Estado e Vendedor</h2>
      
      {/* Exibição da legenda superior */}
      <div className="text-sm text-gray-400 h-14 mb-4 flex flex-col justify-center items-center gap-1">
        {alfineteSelecionado ? (
          <span className="bg-gray-800 px-3 py-1 rounded-full border border-gray-700 text-white">
            📍 Venda de <strong style={{ color: alfineteSelecionado.cor }}>{alfineteSelecionado.nome}</strong> em {alfineteSelecionado.cidade} ({alfineteSelecionado.valor})
          </span>
        ) : estadoSelecionado ? (
          <span className="bg-gray-800 px-3 py-1 rounded-full border border-gray-700">
            <strong>{estadoSelecionado.nome} ({estadoSelecionado.sigla})</strong>: {estadoSelecionado.valor} vendas cadastradas
          </span>
        ) : (
          <>
            <span className="text-gray-500 italic">Passe o mouse sobre os estados ou alfinetes</span>
            {/* Mini Legenda de Vendedores */}
            <div className="flex gap-4 mt-2 text-xs">
              <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-full bg-[#00E5FF]"></span> Carlos</span>
              <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-full bg-[#FF007F]"></span> Ana</span>
              <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-full bg-[#FFD700]"></span> Bruno</span>
              <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-full bg-[#7000FF]"></span> Mariana</span>
            </div>
          </>
        )}
      </div>

      {/* Container do Mapa */}
      <div className="bg-[#0d1117] rounded-lg p-4 border border-gray-800 shadow-2xl flex justify-center items-center">
        <ComposableMap
          projection="geoMercator"
          projectionConfig={{
            center: [-55, -15],
            scale: 620
          }}
          width={500}
          height={500}
          style={{ width: "100%", height: "auto" }}
        >
          {/* 2. Renderização das Fronteiras Estaduais */}
          <Geographies geography={geoUrl}>
            {({ geographies }) =>
              geographies.map((geo) => {
                const sigla = geo.properties.sigla;
                const nome = geo.properties.name;
                const valor = dadosEstados[sigla] || 0;

                return (
                  <Geography
                    key={geo.rsmKey}
                    geography={geo}
                    fill={colorScale(valor)}
                    stroke="#0d1117"
                    strokeWidth={1}
                    style={{
                      default: { outline: "none" },
                      hover: { fill: "#1c2430", outline: "none", cursor: "pointer" },
                      pressed: { fill: "#11161d", outline: "none" }
                    }}
                    onMouseEnter={() => setEstadoSelecionado({ nome, sigla, valor })}
                    onMouseLeave={() => setEstadoSelecionado(null)}
                  />
                );
              })
            }
          </Geographies>

          {/* 3. Renderização dos Alfinetes (Markers) */}
          {vendasVendedores.map((venda, idx) => (
            <Marker 
              key={idx} 
              coordinates={venda.coordenadas}
              onMouseEnter={() => setAlfineteSelecionado(venda)}
              onMouseLeave={() => setAlfineteSelecionado(null)}
            >
              {/* Sombra / Efeito Pulse do Pin */}
              <circle r={7} fill={venda.cor} opacity={0.3} className="animate-ping" />
              
              {/* Miolo do Alfinete */}
              <circle r={4.5} fill={venda.cor} stroke="#0d1117" strokeWidth={1} style={{ cursor: "pointer" }} />
              
              {/* Opcional: Nome minúsculo flutuante opcional ao lado do ponto */}
              <text
                textAnchor="middle"
                y={-10}
                style={{
                  fontFamily: "monospace",
                  fontSize: "8px",
                  fill: "#fff",
                  fontWeight: "bold",
                  pointerEvents: "none",
                  textShadow: "1px 1px 2px #000"
                }}
              >
                {venda.nome}
              </text>
            </Marker>
          ))}
        </ComposableMap>
      </div>
    </div>
  );
}